"use client";

import { FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { handlePasswordResetRequest } from '@/app/auth/actions';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export default function ForgotPasswordForm({ onBackToLogin }: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(event.currentTarget);
      const result = await handlePasswordResetRequest(formData);

      if (result.success) {
        setIsSubmitted(true);
        // toast.success(result.message);
      } else {
        toast.error(result.message || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('Unexpected error during password reset request:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="w-full max-w-md p-6 space-y-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Check your email</h3>
            <p className="text-sm text-[var(--text-primary-alpha)] mt-2">
              If an account with that email exists, we&apos;ve sent you password reset instructions.
            </p>
          </div>
          <Button
            onClick={onBackToLogin}
            className="w-full bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Back to Login
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md p-6 space-y-4">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">Reset your password</h3>
        <p className="text-sm text-[var(--text-primary-alpha)]">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[var(--text-primary)]">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
            autoComplete="email"
            required
            disabled={isLoading}
          />
        </div>
        
        <Button
          type="submit"
          className="w-full bg-[var(--secondary-color)] text-[var(--input-text-color)] hover:text-[var(--input-text-color)] hover:bg-[var(--secondary-color)] hover:opacity-80"
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
      
      <div className="text-center">
        <button
          onClick={onBackToLogin}
          className="text-sm text-gray-500 hover:text-gray-700"
          disabled={isLoading}
        >
          Back to Login
        </button>
      </div>
    </Card>
  );
}